/**
 * Sandbox for platform JS (`En.run`, client-import).
 * Does not replace `window.console` or `window.alert`.
 *
 * Enable logs: `localStorage.setItem('debug_script_console', '1')`.
 * `en.alert` / bare `alert(...)` never open a dialog.
 */
export const PLATFORM_SCRIPT_CONSOLE_WINDOW_KEY = '__platformScriptConsole'
export const PLATFORM_SCRIPT_CONSOLE_STORAGE_KEY = 'debug_script_console'
export const PLATFORM_SCRIPT_CONSOLE_LOG_PREFIX = '[script]'
export const PLATFORM_SCRIPT_ALERT_WINDOW_KEY = '__platformScriptAlert'
export const PLATFORM_SCRIPT_ALERT_LOG_PREFIX = '[en.alert]'

export const PLATFORM_SCRIPT_CONSOLE_PREAMBLE =
  `var console = window.${PLATFORM_SCRIPT_CONSOLE_WINDOW_KEY};\n` +
  `var alert = window.${PLATFORM_SCRIPT_ALERT_WINDOW_KEY} || function () {};\n`

const nativeConsole: Console = globalThis.console
const noop = (): void => {}

const PREFIX_METHODS = new Set(['log', 'info', 'warn', 'error', 'debug'])

type ScriptConsoleHost = Window & typeof globalThis & {
  [PLATFORM_SCRIPT_CONSOLE_WINDOW_KEY]?: Console
  [PLATFORM_SCRIPT_ALERT_WINDOW_KEY]?: PlatformScriptAlert
}

export type PlatformScriptAlert = (message?: unknown) => void

export function isPlatformScriptConsoleEnabled(): boolean {
  try {
    return localStorage.getItem(PLATFORM_SCRIPT_CONSOLE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function createPlatformScriptConsole(): Console {
  return new Proxy({} as Console, {
    get(_target, prop) {
      if (!isPlatformScriptConsoleEnabled()) {
        return noop
      }
      const value = Reflect.get(nativeConsole, prop, nativeConsole)
      if (typeof value !== 'function') {
        return value
      }
      if (typeof prop === 'string' && PREFIX_METHODS.has(prop)) {
        return value.bind(nativeConsole, PLATFORM_SCRIPT_CONSOLE_LOG_PREFIX)
      }
      return value.bind(nativeConsole)
    },
  })
}

/** Debug leftover. No browser dialog and no Messages dialog. */
export function reportPlatformScriptAlert(message: unknown): void {
  if (!isPlatformScriptConsoleEnabled()) {
    return
  }
  console.log(PLATFORM_SCRIPT_ALERT_LOG_PREFIX, String(message))
}

export function installPlatformScriptAlert(
  target: ScriptConsoleHost = window as ScriptConsoleHost,
): PlatformScriptAlert {
  const existing = target[PLATFORM_SCRIPT_ALERT_WINDOW_KEY]
  if (existing) {
    return existing
  }
  const shim: PlatformScriptAlert = (message) => {
    reportPlatformScriptAlert(message)
  }
  target[PLATFORM_SCRIPT_ALERT_WINDOW_KEY] = shim
  return shim
}

export function installPlatformScriptConsole(
  target: ScriptConsoleHost = window as ScriptConsoleHost,
): Console {
  installPlatformScriptAlert(target)
  const existing = target[PLATFORM_SCRIPT_CONSOLE_WINDOW_KEY]
  if (existing) {
    return existing
  }
  const shim = createPlatformScriptConsole()
  target[PLATFORM_SCRIPT_CONSOLE_WINDOW_KEY] = shim
  return shim
}

if (typeof window !== 'undefined') {
  installPlatformScriptConsole()
}
